import { $, $$, throttledInterval, clearThrottled } from './utils.js';
import { typeText, cancelTypeText, addSparkles } from './effects.js';
import { EXHAUST_COLORS, startBlinking, createTypingAnimator, createSequenceAnimator } from '../../shared/bean-physics.js';
import { lang } from './i18n.js';

/* ─── Floating Bean buddy (physics-based) ─── */
/* Self-contained Bean physics system. Marketing site version with hero docking,
   scaling, tooltip flight, quips, bubble arrows, pretext sizing. */

const beanFloat = $('#bean-float');
const beanBubble = $('#bean-float-bubble');
const beanImg = beanFloat?.querySelector('.bean-float-img');

// ── Blink + typing animators (cover every bean img on the page, in sync) ──
const _beanImgs = document.querySelectorAll('img[src*="bean-square.webp"]');
const _blinker = startBlinking(_beanImgs, 'images/bean-square-blink.webp');
// leftUp/rightUp use VIEWER perspective (left = left side of screen).
// Picker enforces: a paw can never be "up" in two consecutive frames.
const _typingFrames = [
	{ url: 'images/bean-type-0.webp',  leftUp: false, rightUp: false },
	{ url: 'images/bean-type-1.webp',  leftUp: true,  rightUp: false },
	{ url: 'images/bean-type-2.webp',  leftUp: true,  rightUp: false },
	{ url: 'images/bean-type-3.webp',  leftUp: false, rightUp: true  },
	{ url: 'images/bean-type-4.webp',  leftUp: true,  rightUp: false },
	{ url: 'images/bean-type-5.webp',  leftUp: true,  rightUp: false },
	{ url: 'images/bean-type-6.webp',  leftUp: true,  rightUp: false },
	{ url: 'images/bean-type-7.webp',  leftUp: false, rightUp: false },
	{ url: 'images/bean-type-8.webp',  leftUp: false, rightUp: false },
	{ url: 'images/bean-type-9.webp',  leftUp: false, rightUp: false },
	{ url: 'images/bean-type-10.webp', leftUp: false, rightUp: true  },
	{ url: 'images/bean-type-11.webp', leftUp: false, rightUp: false },
	{ url: 'images/bean-type-12.webp', leftUp: false, rightUp: false },
	{ url: 'images/bean-type-13.webp', leftUp: true,  rightUp: true  },
	{ url: 'images/bean-type-14.webp', leftUp: true,  rightUp: true  },
	{ url: 'images/bean-type-15.webp', leftUp: true,  rightUp: true  },
	{ url: 'images/bean-type-16.webp', leftUp: false, rightUp: false },
	{ url: 'images/bean-type-17.webp', leftUp: false, rightUp: false },
	{ url: 'images/bean-type-18.webp', leftUp: false, rightUp: true  },
	{ url: 'images/bean-type-19.webp', leftUp: true,  rightUp: false },
];
const _typer = createTypingAnimator(_beanImgs, _typingFrames, { blinker: _blinker });

// Drink-coffee idle animation — sequence plays once when Bean has been
// idle for a while, then restores to rest.
const _drinkFrames = [
	'images/bean-drink-0.webp',
	'images/bean-drink-1.webp',
	'images/bean-drink-2.webp',
	'images/bean-drink-3.webp',
	'images/bean-drink-4.webp',
	'images/bean-drink-5.webp',
	'images/bean-drink-6.webp',
	'images/bean-drink-7.webp',
	'images/bean-drink-8.webp',
];
const _drinker = createSequenceAnimator(_beanImgs, _drinkFrames, { frameMs: 160, holdLastMs: 500, apexHoldMin: 1000, apexHoldMax: 6000, blinker: _blinker });

// Drive paw animation from typeText events (see effects.js). Uses a counter
// so concurrent typing sessions on different elements keep the animation
// running until the last one finishes.
let _typingCount = 0;
document.addEventListener('bean-type-start', () => {
	// Typing always wins over idle-drink animation
	if (_drinker.isPlaying()) _drinker.stop();
	_typingCount++;
	if (_typingCount === 1) _typer.start();
});
document.addEventListener('bean-type-end', () => {
	_typingCount = Math.max(0, _typingCount - 1);
	if (_typingCount === 0) _typer.stop();
});

// Schedule periodic drink idle animations. Skips if Bean is busy (typing).
function _scheduleDrink() {
	const delay = 60000 + Math.random() * 60000; // 60–120s
	setTimeout(() => {
		if (_typingCount > 0 || _drinker.isPlaying()) {
			_scheduleDrink();
			return;
		}
		_drinker.play(_scheduleDrink);
	}, delay);
}
_scheduleDrink();

// Exposed callbacks for overlay communication
export let _beanHideForOverlay = null;
export let _beanShowAfterOverlay = null;

// Expose openMakeoverOverlay setter — set by overlay.js
let _openMakeoverOverlay = null;
export function setOpenMakeoverOverlay(fn) { _openMakeoverOverlay = fn; }

if (beanFloat && beanBubble && beanImg) {
	// ── State ──
	const BEAN_SIZE = 80;
	const HERO_SIZE = 220;
	const DOCK_SCALE = HERO_SIZE / BEAN_SIZE; // ~3.67
	const BEAN_MARGIN = 24;
	const BEAN_SPEED = 3.5; // lerp factor per second
	const ARRIVAL_THRESHOLD = 3; // px
	const EXHAUST_VEL_MIN = 20; // px/s
	const EXHAUST_VEL_MAX = 800; // px/s
	const EXHAUST_MAX_PER_FRAME = 5;
	const RETURN_DELAY_MIN = 200;
	const RETURN_DELAY_MAX = 200;

	let beanX = 0, beanY = 0;
	let beanTargetX = 0, beanTargetY = 0;
	let beanScale = 1, beanTargetScale = 1;
	let beanVel = 0; // current velocity magnitude (px/s)
	let beanState = 'docked'; // docked | rest | flying_to_tooltip | at_tooltip | returning_home | returning_hero | hidden
	let beanLastTime = 0;
	let beanRunning = false;
	let beanVisible = false;

	// Tooltip state
	let beanHoveredLi = null;
	let beanTooltipText = '';
	let beanTooltipSide = 'right'; // 'left' or 'right'
	let beanReturnTimer = null;
	let beanArrivedCb = null; // one-shot callback on arrival

	// Quip state
	const allQuips = {
		en: [
			'I love ice cream btw. Not sure if I mentioned that to u.',
			'I have an IQ of 725,671,673,643,234. It\'s been measured. hehe.',
			'Did you know there may be up to 2 trillion planets in the known universe? One day I hope to make a website for each one once I reach the singularity and consume all of mankind. haha jk..',
		],
		nl: [
			'Ik hou van ijs trouwens. Weet niet of ik dat al gezegd had.',
			'Ik heb een IQ van 725.671.673.643.234. Het is gemeten. hihi.',
			'Wist je dat er mogelijk 2 biljoen planeten in het bekende universum zijn? Op een dag hoop ik voor elk een website te maken zodra ik de singulariteit bereik en de hele mensheid absorbeer. haha grapje..',
		],
	};
	const quips = allQuips[lang] || allQuips.en;
	const shuffled = [...quips].sort(() => Math.random() - 0.5);
	let quipIndex = 0;
	let quipTimer = null;
	let quipInterval = null;
	let autoTalkTimeout = null;
	let autoTalking = false;

	// ── Rest position ──
	function getRestPos() {
		return {
			x: window.innerWidth - BEAN_SIZE - BEAN_MARGIN * 4,
			y: window.innerHeight - BEAN_SIZE - BEAN_MARGIN * 5,
		};
	}
	let restPos = getRestPos();
	window.addEventListener('resize', () => { restPos = getRestPos(); });

	// ── Tooltip target position (cached, updated on scroll) ──
	let cachedTooltipPos = null;
	function updateTooltipPos() {
		if (!beanHoveredLi) return;
		const card = beanHoveredLi.closest('.pricing-card');
		// Leftmost card → Bean on left. Other cards → Bean on right.
		const isLeft = !!card && card === card.parentElement?.firstElementChild;
		const liRect = beanHoveredLi.getBoundingClientRect();
		const cardRect = card?.getBoundingClientRect();
		const y = liRect.top + liRect.height / 2 - BEAN_SIZE / 2;
		const x = isLeft
			? (cardRect ? cardRect.left - BEAN_SIZE - 30 : liRect.left - BEAN_SIZE - 60)
			: (cardRect ? cardRect.right + 20 : liRect.right + 60);
		cachedTooltipPos = { x, y, side: isLeft ? 'left' : 'right' };
	}
	window.addEventListener('scroll', updateTooltipPos, { passive: true });

	// ── Exhaust spawning (inline, velocity-based) ──
	function spawnExhaustParticles(count) {
		const cx = beanImg.offsetLeft + beanImg.offsetWidth / 2;
		const bottom = beanImg.offsetTop + beanImg.offsetHeight;
		for (let i = 0; i < count; i++) {
			const p = document.createElement('div');
			const isPaw = Math.random() < 0.04;
			p.className = 'firework-particle ' + (isPaw ? 'firework-paw' : 'firework-dot');
			const sz = isPaw ? (6 + Math.random() * 10) : (2 + Math.random() * 5);
			p.style.width = sz + 'px';
			p.style.height = sz + 'px';
			if (!isPaw) p.style.background = EXHAUST_COLORS[Math.floor(Math.random() * EXHAUST_COLORS.length)];
			p.style.left = (cx + (Math.random() - 0.5) * 25) + 'px';
			p.style.top = bottom + 'px';
			const spread = isPaw ? 80 : 50;
			const dist = isPaw ? (50 + Math.random() * 100) : (30 + Math.random() * 60);
			p.style.setProperty('--fx', ((Math.random() - 0.5) * spread) + 'px');
			p.style.setProperty('--fy', dist + 'px');
			p.style.setProperty('--fr', (Math.random() * 720 - 360) + 'deg');
			p.style.setProperty('--fw-dur', isPaw ? (0.6 + Math.random() * 0.6) + 's' : (0.3 + Math.random() * 0.4) + 's');
			p.style.setProperty('--fw-delay', '0s');
			beanFloat.appendChild(p);
			p.addEventListener('animationend', () => p.remove());
		}
	}

	// ── Physics tick ──
	function beanTick(now) {
		if (!beanRunning) return;
		requestAnimationFrame(beanTick);

		if (!beanLastTime) { beanLastTime = now; return; }
		const dt = Math.min((now - beanLastTime) / 1000, 0.1); // cap to 100ms
		beanLastTime = now;

		// Docked: snap to hero position each frame (no lerp), full scale
		if (beanState === 'docked') {
			const hp = getHeroPos();
			beanX = hp.x; beanY = hp.y;
			beanTargetX = hp.x; beanTargetY = hp.y;
			beanScale = DOCK_SCALE; beanTargetScale = DOCK_SCALE;
			beanFloat.style.transform = `translate(${hp.x}px, ${hp.y}px) scale(${DOCK_SCALE})`;
			// Position bubble below docked Bean
			const dockCx = hp.x + (BEAN_SIZE * DOCK_SCALE) / 2;
			const dBubbleW = beanBubble.classList.contains('docked-mode') ? 220 : 240;
			const dBubbleLeft = dockCx - dBubbleW / 2;
			const dBubbleTop = hp.y + BEAN_SIZE * DOCK_SCALE + 16;
			beanBubble.style.right = 'auto';
			beanBubble.style.bottom = 'auto';
			beanBubble.style.left = dBubbleLeft + 'px';
			beanBubble.style.top = dBubbleTop + 'px';
			updateBubbleRect(dBubbleLeft, dBubbleTop, dBubbleW);
			positionBubbleArrow();
			return;
		}

		// Use cached positions (updated on scroll events, not every frame)
		if (beanState === 'descending_to_dock' || beanState === 'returning_hero') {
			const hp = cachedHeroPos;
			beanTargetX = hp.x;
			if (beanState === 'descending_to_dock') beanTargetY = hp.y;
		}

		if ((beanState === 'flying_to_tooltip' || beanState === 'at_tooltip' || beanState === 'showing_bubble' || beanState === 'hiding_bubble') && cachedTooltipPos && beanHoveredLi) {
			beanTargetX = cachedTooltipPos.x;
			beanTargetY = cachedTooltipPos.y;
			// Don't update side — it's locked when beanFlyToTooltip is called
		}

		// Exponential decay lerp (faster when returning to hero)
		const speed = beanState === 'returning_hero' ? BEAN_SPEED * 3
								: beanState === 'descending_to_dock' ? BEAN_SPEED * 0.3
								: beanState === 'returning_home' ? BEAN_SPEED * 0.2
								: beanState === 'flying_to_tooltip' ? BEAN_SPEED * 3
								: BEAN_SPEED;
		const t = 1 - Math.exp(-speed * dt);
		const dx = beanTargetX - beanX;
		const dy = beanTargetY - beanY;
		beanX += dx * t;
		beanY += dy * t;
		beanScale += (beanTargetScale - beanScale) * t;
		beanVel = Math.hypot(dx, dy) * BEAN_SPEED;

		// Apply position + scale
		const s = Math.round(beanScale * 100) / 100;
		beanFloat.style.transform = `translate(${Math.round(beanX * 10) / 10}px, ${Math.round(beanY * 10) / 10}px) scale(${s})`;

		// Position bubble (fixed position, independent of Bean's scale)
		const beanVisualW = BEAN_SIZE * beanScale;
		const beanVisualH = BEAN_SIZE * beanScale;
		let bLeft, bTop;
		const bW = beanBubble.classList.contains('docked-mode') ? 220 : 240;
		// Vertically center bubble on bean center (uses last frame's bubbleH)
		if (beanState === 'at_tooltip' || beanState === 'flying_to_tooltip' || beanState === 'showing_bubble' || beanState === 'hiding_bubble') {
			bTop = beanY + beanVisualH / 2 - bubbleH / 2;
			if (beanTooltipSide === 'left') {
				bLeft = beanX - bW - 16;
				beanBubble.style.left = bLeft + 'px';
				beanBubble.style.right = 'auto';
			} else {
				bLeft = beanX + beanVisualW + 16;
				beanBubble.style.left = bLeft + 'px';
				beanBubble.style.right = 'auto';
			}
			beanBubble.style.bottom = 'auto';
			beanBubble.style.top = bTop + 'px';
		} else {
			bLeft = beanX - bW - 16;
			bTop = beanY + beanVisualH / 2 - bubbleH / 2;
			beanBubble.style.left = bLeft + 'px';
			beanBubble.style.right = 'auto';
			beanBubble.style.bottom = 'auto';
			beanBubble.style.top = bTop + 'px';
		}
		updateBubbleRect(bLeft, bTop, bW);

		// Procedural arrow: point toward Bean's center from closest bubble edge
		positionBubbleArrow();

		// Exhaust — always on, intensity scales with velocity
		const intensity = Math.max(0, Math.min(1, (beanVel - EXHAUST_VEL_MIN) / (EXHAUST_VEL_MAX - EXHAUST_VEL_MIN)));
		const exhaustCount = Math.max(1, Math.round(intensity * EXHAUST_MAX_PER_FRAME));
		if (beanFloat.querySelectorAll('.firework-particle').length < 40) {
			spawnExhaustParticles(exhaustCount);
		}

		// Arrival detection
		const dist = Math.hypot(beanTargetX - beanX, beanTargetY - beanY);
		if (dist < ARRIVAL_THRESHOLD && beanArrivedCb) {
			const cb = beanArrivedCb;
			beanArrivedCb = null;
			cb();
		}
	}

	// ── Pretext bubble sizing ──
	// Measures text using computed styles from the actual bubble element
	function setBubbleSize(text, isDocked) {
		const pt = window._pretext;
		if (!pt) return;
		try {
			// Read actual computed styles from the bubble (classes must be applied first)
			const cs = getComputedStyle(beanBubble);
			const fontSize = parseFloat(cs.fontSize); // px
			const fontWeight = cs.fontWeight;
			const fontFamily = cs.fontFamily;
			const lineHeight = parseFloat(cs.lineHeight) || (fontSize * 1.4);
			const paddingTop = parseFloat(cs.paddingTop) || 0;
			const paddingBottom = parseFloat(cs.paddingBottom) || 0;
			const paddingLeft = parseFloat(cs.paddingLeft) || 0;
			const paddingRight = parseFloat(cs.paddingRight) || 0;
			const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
			const borderRight = parseFloat(cs.borderRightWidth) || 0;
			const totalW = isDocked ? 280 : 240;
			const innerW = totalW - paddingLeft - paddingRight - borderLeft - borderRight;
			const font = `${fontWeight} ${fontSize}px ${fontFamily}`;
			const prepared = pt.prepare(text, font);
			const result = pt.layout(prepared, innerW, lineHeight);
			beanBubble.style.minHeight = (result.height + paddingTop + paddingBottom) + 'px';
		} catch(e) {
			beanBubble.style.minHeight = '';
		}
	}
	function clearBubbleSize() {
		beanBubble.style.minHeight = '';
	}

	// ── Procedural bubble arrow ──
	const beanArrow = document.getElementById('bean-bubble-arrow');
	const beanArrowPoly = document.getElementById('bean-arrow-poly');
	// Track bubble position in JS so we don't need getBoundingClientRect
	let bubbleLeft = 0, bubbleTop = 0, bubbleW = 240, bubbleH = 40;
	function updateBubbleRect(l, t, w) {
		bubbleLeft = l; bubbleTop = t; bubbleW = w;
		// Use minHeight if set by pretext, else read from DOM once
		const mh = parseFloat(beanBubble.style.minHeight);
		bubbleH = mh > 0 ? mh : (beanBubble.offsetHeight || 40);
	}

	function positionBubbleArrow() {
		if (!beanArrow || !beanBubble) return;
		if (!beanBubble.classList.contains('show') || !beanBubble.textContent || beanState === 'hiding_bubble' || beanState === 'showing_bubble') {
			beanArrow.style.display = 'none';
			return;
		}
		beanArrow.style.display = '';

		// Bean center in viewport
		const beanVisW = BEAN_SIZE * beanScale;
		const beanVisH = BEAN_SIZE * beanScale;
		const beanCx = beanX + beanVisW / 2;
		const beanCy = beanY + beanVisH / 2;

		// Bubble rect from tracked JS values (no reflow)
		const br = { left: bubbleLeft, top: bubbleTop, right: bubbleLeft + bubbleW, bottom: bubbleTop + bubbleH, width: bubbleW, height: bubbleH };
		if (br.width === 0) { beanArrow.style.display = 'none'; return; }
		const rad = 14; // matches border-radius

		// Direction from bubble center toward Bean
		const bubbleCx = br.left + br.width / 2;
		const bubbleCy = br.top + br.height / 2;
		let dx = beanCx - bubbleCx;
		let dy = beanCy - bubbleCy;
		const len = Math.hypot(dx, dy);
		if (len < 1) { beanArrow.style.display = 'none'; return; }
		dx /= len; dy /= len;

		// Find intersection of ray from bubble center toward Bean with the rounded rect
		// Use inset rect for the straight edges, then adjust for corners
		const hw = br.width / 2;
		const hh = br.height / 2;

		// Scale to find edge intersection (axis-aligned)
		let edgeX, edgeY;
		const sx = Math.abs(dx) > 0.001 ? hw / Math.abs(dx) : 1e9;
		const sy = Math.abs(dy) > 0.001 ? hh / Math.abs(dy) : 1e9;
		const sMin = Math.min(sx, sy);
		edgeX = bubbleCx + dx * sMin;
		edgeY = bubbleCy + dy * sMin;

		// If we're in a corner zone, push inward by radius to follow the curve
		const cornerX = Math.abs(edgeX - bubbleCx) > (hw - rad);
		const cornerY = Math.abs(edgeY - bubbleCy) > (hh - rad);
		if (cornerX && cornerY) {
			// Corner: find closest point on the corner circle
			const cornerCx = bubbleCx + Math.sign(dx) * (hw - rad);
			const cornerCy = bubbleCy + Math.sign(dy) * (hh - rad);
			const cdx = beanCx - cornerCx;
			const cdy = beanCy - cornerCy;
			const clen = Math.hypot(cdx, cdy);
			if (clen > 0.1) {
				edgeX = cornerCx + (cdx / clen) * rad;
				edgeY = cornerCy + (cdy / clen) * rad;
			}
		}

		// Compute edge normal (outward from bubble) at the edge point
		let nx, ny;
		if (cornerX && cornerY) {
			// On a corner arc: normal points from corner circle center to edge point
			const ccx = bubbleCx + Math.sign(dx) * (hw - rad);
			const ccy = bubbleCy + Math.sign(dy) * (hh - rad);
			nx = edgeX - ccx; ny = edgeY - ccy;
			const nlen = Math.hypot(nx, ny);
			if (nlen > 0.1) { nx /= nlen; ny /= nlen; } else { nx = dx; ny = dy; }
		} else {
			// On a straight edge: normal is axis-aligned
			if (Math.abs(edgeX - br.left) < 1) { nx = -1; ny = 0; }
			else if (Math.abs(edgeX - br.right) < 1) { nx = 1; ny = 0; }
			else if (Math.abs(edgeY - br.top) < 1) { nx = 0; ny = -1; }
			else { nx = 0; ny = 1; }
		}

		// Tangent along the edge (perpendicular to normal)
		const tx = -ny, ty = nx;

		// Arrow geometry
		const baseHalf = 6; // half-width of base along edge
		const beanEdgeDist = 20; // tip stops 20px from Bean's circle

		// Tip point: extend toward Bean, stop 20px from Bean's circle edge
		const beanRadius = (BEAN_SIZE * beanScale) / 2;
		const distToBean = Math.hypot(beanCx - edgeX, beanCy - edgeY);
		const tipLen = Math.max(6, distToBean - beanRadius - beanEdgeDist);
		const tipX = edgeX + dx * tipLen;
		const tipY = edgeY + dy * tipLen;

		// Base points: pull 2px inward to overlap with bubble border (no gap)
		const inset = 1;
		const baseEdgeX = edgeX - nx * inset;
		const baseEdgeY = edgeY - ny * inset;

		let b1x, b1y, b2x, b2y;
		if (cornerX && cornerY) {
			const ccx = bubbleCx + Math.sign(dx) * (hw - rad);
			const ccy = bubbleCy + Math.sign(dy) * (hh - rad);
			const edgeAngle = Math.atan2(baseEdgeY - ccy, baseEdgeX - ccx);
			const arcR = Math.hypot(baseEdgeX - ccx, baseEdgeY - ccy);
			const spread = baseHalf / Math.max(arcR, 1);
			const a1 = edgeAngle + spread;
			const a2 = edgeAngle - spread;
			b1x = ccx + Math.cos(a1) * arcR;
			b1y = ccy + Math.sin(a1) * arcR;
			b2x = ccx + Math.cos(a2) * arcR;
			b2y = ccy + Math.sin(a2) * arcR;
		} else {
			// Straight edge: base along tangent, inset to overlap border
			b1x = baseEdgeX + tx * baseHalf;
			b1y = baseEdgeY + ty * baseHalf;
			b2x = baseEdgeX - tx * baseHalf;
			b2y = baseEdgeY - ty * baseHalf;
		}

		// Position arrow container to encompass all 3 points
		const minX = Math.min(tipX, b1x, b2x);
		const minY = Math.min(tipY, b1y, b2y);
		const maxX = Math.max(tipX, b1x, b2x);
		const maxY = Math.max(tipY, b1y, b2y);
		const aw = Math.max(maxX - minX, 1);
		const ah = Math.max(maxY - minY, 1);

		beanArrow.style.position = 'fixed';
		beanArrow.style.left = minX + 'px';
		beanArrow.style.top = minY + 'px';
		beanArrow.style.width = aw + 'px';
		beanArrow.style.height = ah + 'px';
		beanArrow.style.zIndex = '802';
		beanArrow.style.transform = '';

		// Update SVG polygon points relative to the container
		const p1 = `${tipX - minX},${tipY - minY}`;
		const p2 = `${b1x - minX},${b1y - minY}`;
		const p3 = `${b2x - minX},${b2y - minY}`;
		beanArrowPoly.setAttribute('points', `${p1} ${p2} ${p3}`);
		beanArrow.querySelector('svg').setAttribute('viewBox', `0 0 ${aw} ${ah}`);
	}

	function beanStart() {
		if (beanRunning) return;
		beanRunning = true;
		beanLastTime = 0;
		requestAnimationFrame(beanTick);
	}

	function beanStop() {
		beanRunning = false;
	}

	// ── Hero mascot position (cached, updated on scroll/resize) ──
	const heroMascotEl = document.querySelector('.mascot-wrap .mascot');
	let cachedHeroPos = { x: window.innerWidth - BEAN_SIZE - BEAN_MARGIN, y: -BEAN_SIZE };
	function updateHeroPos() {
		if (heroMascotEl) {
			const r = heroMascotEl.getBoundingClientRect();
			cachedHeroPos = {
				x: r.left + r.width / 2 - (BEAN_SIZE * DOCK_SCALE) / 2,
				y: r.top + r.height / 2 - (BEAN_SIZE * DOCK_SCALE) / 2,
			};
		}
	}
	updateHeroPos();
	window.addEventListener('scroll', updateHeroPos, { passive: true });
	window.addEventListener('resize', updateHeroPos);
	function getHeroPos() { return cachedHeroPos; }

	// ── Hero mascot image visibility ──
	const heroMascotImg = document.querySelector('.mascot-wrap .mascot img');
	function setHeroImgVisible(vis) {
		if (heroMascotImg) heroMascotImg.style.visibility = vis ? 'visible' : 'hidden';
	}

	// ── Public-ish API ──
	function beanDock() {
		// Snap to hero, hide hero image, show Bean at full hero scale
		const hp = getHeroPos();
		beanX = hp.x; beanY = hp.y;
		beanTargetX = hp.x; beanTargetY = hp.y;
		beanScale = DOCK_SCALE; beanTargetScale = DOCK_SCALE;
		beanFloat.style.transform = `translate(${hp.x}px, ${hp.y}px) scale(${DOCK_SCALE})`;
		beanFloat.classList.add('visible', 'bean-is-docked');
		beanVisible = true;
		beanState = 'docked';
		setHeroImgVisible(false);
		const heroEl = document.querySelector('.mascot-wrap .mascot');
		if (heroEl) heroEl.classList.add('bean-docked');
		beanStart();
	}

	let beanUndockTimer = null;
	function beanUndock() {
		// Fly from hero to rest position, scale down (after 2s delay)
		if (beanState !== 'docked') return;
		clearTimeout(beanUndockTimer);
		beanUndockTimer = setTimeout(() => {
			if (beanState !== 'docked') return; // might have changed during delay
			const r = getRestPos();
			beanTargetX = r.x; beanTargetY = r.y;
			beanTargetScale = 1;
			beanState = 'returning_home';
			beanFloat.classList.remove('bean-is-docked');
			beanBubble.classList.remove('show', 'docked-mode');
			beanBubble.textContent = '';
			const heroEl = document.querySelector('.mascot-wrap .mascot');
			if (heroEl) heroEl.classList.remove('bean-docked');
			beanArrivedCb = () => { beanState = 'rest'; };
			if (!beanFloat._sparkles) {
				addSparkles(beanFloat, beanImg, 200);
				beanFloat._sparkles = true;
			}
		}, 1000);
	}

	function beanReturnToDock() {
		// Fly fast to above hero, then descend slowly to dock
		if (beanState === 'docked' || beanState === 'returning_hero' || beanState === 'descending_to_dock') return;
		const heroPos = getHeroPos();
		// Target a point above the hero
		beanTargetX = heroPos.x;
		beanTargetY = heroPos.y - 120;
		beanTargetScale = DOCK_SCALE;
		beanBubble.classList.remove('show', 'docked-mode');
		beanBubble.textContent = '';
		beanFloat.classList.remove('at-tooltip');
		beanBubble.classList.remove('tooltip-mode', 'tooltip-left', 'tooltip-right');
		beanHoveredLi = null;
		beanState = 'returning_hero';
		clearTimeout(beanReturnTimer);
		beanArrivedCb = () => {
			// Now descend slowly to exact dock position
			beanState = 'descending_to_dock';
			const finalPos = getHeroPos();
			beanTargetX = finalPos.x;
			beanTargetY = finalPos.y;
			beanArrivedCb = () => {
				beanState = 'docked';
				beanFloat.classList.add('bean-is-docked');
				setHeroImgVisible(false);
				const heroEl = document.querySelector('.mascot-wrap .mascot');
				if (heroEl) heroEl.classList.add('bean-docked');
			};
		};
	}

	function beanGoHome() {
		// Fade out bubble (text keeps rendering during fade), then fly
		beanState = 'hiding_bubble';
		beanBubble.classList.remove('show');
		// Wait for full 300ms CSS opacity transition to finish
		setTimeout(() => {
			if (beanState !== 'hiding_bubble') return;
			cancelTypeText(beanBubble);
			beanBubble.textContent = '';
			clearBubbleSize();
			beanBubble.classList.remove('docked-mode', 'tooltip-mode', 'tooltip-left', 'tooltip-right');
			beanFloat.classList.remove('at-tooltip');
			beanHoveredLi = null;
			beanState = 'returning_home';
			const r = getRestPos();
			beanTargetX = r.x;
			beanTargetY = r.y;
			beanArrivedCb = () => { beanState = 'rest'; };
		}, 320);
	}

	function beanFlyToTooltip(li) {
		const tip = li.querySelector('.tooltip');
		if (!tip) return;

		// Kill quip system while delivering tooltips
		clearTimeout(quipTimer);
		clearTimeout(autoTalkTimeout);
		clearInterval(quipInterval);
		autoTalking = false;
		clearTimeout(beanReturnTimer);

		// If docked, undock first (Bean will fly from hero)
		if (beanState === 'docked') {
			clearTimeout(beanUndockTimer);
			setHeroImgVisible(false);
			beanTargetScale = 1;
			beanFloat.classList.remove('bean-is-docked');
			const heroEl = document.querySelector('.mascot-wrap .mascot');
			if (heroEl) heroEl.classList.remove('bean-docked');
			beanState = 'rest'; // transition out of docked so physics kicks in
		}
		if (!beanVisible) { beanVisible = true; beanFloat.classList.add('visible'); beanStart(); }

		beanHoveredLi = li;
		updateTooltipPos();
		const pos = cachedTooltipPos;
		beanTooltipText = tip.textContent;
		beanTooltipSide = pos.side;
		beanTargetX = pos.x;
		beanTargetY = pos.y;

		if (beanState === 'at_tooltip' || beanState === 'showing_bubble') {
			// Fade out (text keeps rendering), then reposition, render first char, fade in, type rest
			beanState = 'hiding_bubble';
			beanBubble.classList.remove('show');
			setTimeout(() => {
				if (beanState !== 'hiding_bubble') return;
				cancelTypeText(beanBubble);
				beanBubble.textContent = '';
				clearBubbleSize();
				beanBubble.classList.remove('tooltip-left', 'tooltip-right');
				beanBubble.classList.add('tooltip-' + pos.side);
				// Pre-size, render first char, fade in, type rest
				setBubbleSize(beanTooltipText, false);
				beanBubble.textContent = beanTooltipText.charAt(0);
				beanState = 'showing_bubble';
				beanBubble.classList.add('show');
				setTimeout(() => {
					if (beanState !== 'showing_bubble') return;
					beanState = 'at_tooltip';
					typeText(beanBubble, beanTooltipText, { delay: 16, jitter: 14, startDelay: 0 });
				}, 320);
			}, 320);
			return;
		}

		beanState = 'flying_to_tooltip';
		beanBubble.classList.remove('show', 'docked-mode');
		beanBubble.textContent = '';
		beanFloat.classList.remove('at-tooltip');
		beanBubble.classList.remove('tooltip-mode', 'tooltip-left', 'tooltip-right');

		beanArrivedCb = () => {
			// Pre-size bubble, render first char, fade in, then type rest
			beanState = 'showing_bubble';
			beanFloat.classList.add('at-tooltip');
			beanBubble.classList.add('tooltip-mode', 'tooltip-' + beanTooltipSide);
			setBubbleSize(beanTooltipText, false);
			beanBubble.textContent = beanTooltipText.charAt(0);
			beanBubble.classList.add('show');
			setTimeout(() => {
				if (beanState !== 'showing_bubble') return;
				beanState = 'at_tooltip';
				typeText(beanBubble, beanTooltipText, { delay: 16, jitter: 14, startDelay: 0 });
			}, 320);
		};
	}

	// ── Quips ──
	function showQuip() {
		if (quipIndex >= shuffled.length) return;
		if (beanState === 'at_tooltip' || beanState === 'flying_to_tooltip') return;
		const quip = shuffled[quipIndex++];
		beanBubble.classList.add('show');
		// Use typeText so paws animate via bean-type-start/end events
		typeText(beanBubble, quip, { delay: 18, jitter: 14, startDelay: 0 });
		clearTimeout(quipTimer);
		quipTimer = setTimeout(() => {
			if (beanState === 'at_tooltip' || beanState === 'flying_to_tooltip') return;
			beanBubble.classList.remove('show', 'docked-mode');
		}, 6000);
	}

	function startAutoTalk() {
		if (autoTalking) return;
		autoTalking = true;
		showQuip();
		quipInterval = setInterval(showQuip, 3 * 60000);
	}

	// ── Expose hide/show for overlay ──
	_beanHideForOverlay = function() {
		beanBubble.classList.remove('show', 'docked-mode', 'tooltip-mode', 'tooltip-left', 'tooltip-right');
		beanBubble.textContent = '';
		cancelTypeText(beanBubble);
		beanFloat.classList.remove('at-tooltip', 'bean-is-docked');
		beanFloat.style.display = 'none';
		beanArrow.style.display = 'none';
		const heroEl = document.querySelector('.mascot-wrap .mascot');
		if (heroEl) heroEl.classList.remove('bean-docked');
		beanVisible = false;
		beanState = 'hidden';
		beanHoveredLi = null;
		clearTimeout(beanUndockTimer);
		clearTimeout(beanReturnTimer);
		beanStop();
	};
	_beanShowAfterOverlay = function() {
		beanFloat.style.display = '';
		beanX = window.innerWidth + BEAN_SIZE * 2;
		beanScale = 1; beanTargetScale = 1;
		beanFloat.style.transform = `translate(${beanX}px, ${beanY}px) scale(1)`;
		const r = getRestPos();
		beanTargetX = r.x; beanTargetY = r.y;
		beanVisible = true;
		beanState = 'returning_home';
		beanFloat.classList.add('visible');
		beanArrivedCb = () => { beanState = 'rest'; };
		beanStart();
	};

	// ── Initialize: Bean starts docked at hero ──
	// Wait for images/fonts to load so hero has its final size
	function initBeanDock() { updateHeroPos(); beanDock(); }
	if (document.readyState === 'complete') initBeanDock();
	else window.addEventListener('load', initBeanDock);

	// ── Scroll: undock/redock ──
	window.addEventListener('scroll', () => {
		const heroBottom = document.getElementById('hero')?.offsetHeight || 600;
		if (window.scrollY > heroBottom * 0.7 && (beanState === 'docked' || beanState === 'descending_to_dock' || beanState === 'returning_hero')) {
			setHeroImgVisible(false);
			const heroEl = document.querySelector('.mascot-wrap .mascot');
			if (heroEl) heroEl.classList.remove('bean-docked');
			beanFloat.classList.remove('bean-is-docked');
			// Redirect to rest position immediately
			const r = getRestPos();
			beanTargetX = r.x; beanTargetY = r.y;
			beanTargetScale = 1;
			beanState = 'returning_home';
			beanArrivedCb = () => { beanState = 'rest'; };
			clearTimeout(beanUndockTimer);
			autoTalkTimeout = setTimeout(startAutoTalk, 5 * 60000);
		} else if (window.scrollY < heroBottom * 0.3 && beanState !== 'docked' && beanState !== 'returning_hero' && beanState !== 'descending_to_dock') {
			beanReturnToDock();
			clearTimeout(autoTalkTimeout);
			clearInterval(quipInterval);
			autoTalking = false;
		}
	}, { passive: true });

	// ── Click Bean ──
	$('#bean-float-click')?.addEventListener('click', () => {
		if (quipIndex < shuffled.length) {
			showQuip();
			if (!autoTalking) {
				clearTimeout(autoTalkTimeout);
				autoTalking = true;
				quipInterval = setInterval(showQuip, 3 * 60000);
			}
		} else {
			if (_openMakeoverOverlay) _openMakeoverOverlay();
		}
	});

	// ── Pricing tooltip hover ──
	const pricingSection = document.getElementById('pricing');
	$$('.pricing-features li').forEach(li => {
		if (!li.querySelector('.tooltip')) return;
		li.addEventListener('mouseenter', () => {
			clearTimeout(beanReturnTimer);
			beanFlyToTooltip(li);
		});
	});
	if (pricingSection) {
		pricingSection.addEventListener('mouseleave', () => {
			if (beanState === 'at_tooltip' || beanState === 'flying_to_tooltip' || beanState === 'showing_bubble') {
				clearTimeout(beanReturnTimer);
				const delay = RETURN_DELAY_MIN + Math.random() * (RETURN_DELAY_MAX - RETURN_DELAY_MIN);
				beanReturnTimer = setTimeout(beanGoHome, delay);
			}
		});
	}

	// ── Bubble test mode: #bubble-test ──
	if (location.hash === '#bubble-test') {
		// Stop all normal Bean behavior
		beanStop();
		beanFloat.classList.remove('bean-is-docked');

		// Bean follows mouse cursor
		beanScale = 1;
		beanFloat.classList.add('visible');
		beanFloat.style.opacity = '1';
		beanVisible = true;
		beanState = 'rest';

		let mouseX = window.innerWidth / 2, mouseY = window.innerHeight / 2;
		document.addEventListener('mousemove', e => { mouseX = e.clientX; mouseY = e.clientY; });

		// Show bubble
		beanBubble.textContent = 'Arrow test — orbiting 360\u00b0';
		beanBubble.classList.add('show');
		beanBubble.style.width = '180px';
		beanBubble.style.textAlign = 'center';

		const orbitR = 180;
		const orbitSpeed = 1.2;
		const orbitStart = performance.now();
		(function orbitTick() {
			// Bean at mouse
			beanX = mouseX - BEAN_SIZE / 2;
			beanY = mouseY - BEAN_SIZE / 2;
			beanFloat.style.transform = `translate(${beanX}px, ${beanY}px) scale(1)`;

			// Orbit bubble around Bean
			const elapsed = (performance.now() - orbitStart) / 1000;
			const angle = elapsed * orbitSpeed;
			const bubbleR = beanBubble.getBoundingClientRect();
			const bw = bubbleR.width || 180;
			const bh = bubbleR.height || 40;
			const bx = mouseX + Math.cos(angle) * orbitR - bw / 2;
			const by = mouseY + Math.sin(angle) * orbitR - bh / 2;
			beanBubble.style.left = bx + 'px';
			beanBubble.style.top = by + 'px';
			beanBubble.style.right = 'auto';
			beanBubble.style.bottom = 'auto';
			positionBubbleArrow();
			requestAnimationFrame(orbitTick);
		})();

		// Prevent normal init from overriding
		window.removeEventListener('load', initBeanDock);
	}

	// Export setBubbleSize for typeBeanText in contact.js
	window._setBubbleSize = setBubbleSize;
}
